import { cleanNpcResponse } from '@/lib/filters/cleanNpcResponse';
import { getStaffAvatarUrl, getVideoUrl } from '@/lib/media';
import type { ChatMessage, ChatRoom, LobbyState } from './lobbyStore.types';
import { pruneMessages } from './prune-messages';

/** NPC avatar URLs for pre-made staff avatars (served from GCS) */
const NPC_AVATARS: Record<string, string> = {
  tavian: getStaffAvatarUrl('tavian-avatar.webp'),
  aldric: getStaffAvatarUrl('aldric-avatar.webp'),
  kira: getStaffAvatarUrl('kira-avatar.webp'),
  grolt: getStaffAvatarUrl('grolt-avatar.webp'),
};

/** NPC hero video URLs (served from GCS). Null = no video for this NPC yet. */
const NPC_HERO_VIDEOS: Record<string, string | null> = {
  tavian: getVideoUrl('tavian.mp4'),
  aldric: getVideoUrl('aldric.mp4'),
  kira: getVideoUrl('kira.mp4'),
  grolt: getVideoUrl('grolt.mp4'),
};

/** Maximum messages to keep per chat room to prevent memory exhaustion */
const MAX_MESSAGES_PER_ROOM = 200;

/** Maximum pending messages to buffer per room before the room is created */
const MAX_PENDING_PER_ROOM = 50;

/** localStorage key used to persist the user's last active chat tab across sessions */
const ACTIVE_CHAT_ROOM_STORAGE_KEY = 'lobby-active-chat-room';

/** Save the active chat room ID to localStorage so it can be restored on reconnect */
function persistActiveChatRoom(roomId: string) {
  try {
    localStorage.setItem(ACTIVE_CHAT_ROOM_STORAGE_KEY, roomId);
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded) — ignore
  }
}

/** Read the previously saved active chat room ID from localStorage */
export function getPersistedActiveChatRoom(): string | null {
  try {
    return localStorage.getItem(ACTIVE_CHAT_ROOM_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Check if a saved room ID is safe to restore on reconnect.
 * `char-chat-*` rooms are ephemeral backend sessions that don't survive
 * reconnect — restoring them lands the player in a ghost chat where
 * messages fail with "Chat session not found".
 */
export function isSafeToRestoreRoom(roomId: string): boolean {
  // char-chat-* rooms are ephemeral backend sessions; inner-* rooms are recreated on character hydration
  return !roomId.startsWith('char-chat-') && !roomId.startsWith('inner-');
}

/**
 * Standalone implementation of switchMainNpc logic.
 * Extracted so startAldricChat/startKiraChat/returnToTavian can call it
 * without going through the slice's Pick<LobbyState, ...> typing
 * (which sees the bound action signature, not the transformer).
 */
function switchMainNpcImpl(
  npc: 'tavian' | 'aldric' | 'kira' | 'grolt'
): (state: LobbyState) => Partial<LobbyState> {
  const npcNames: Record<string, string> = {
    tavian: 'Tavian',
    aldric: 'Brother Aldric',
    kira: 'Master Kira',
    grolt: 'Grolt',
  };
  // Instant client-side bubble shown the moment the room switches, while the
  // NPC's real LLM opener generates (~10s). It must BRIDGE that wait, not
  // compete with it: no questions (the real opener asks its own), and an
  // action in progress so the follow-up reads as a continuation.
  //
  // Tavian is the exception: no LLM opener streams after his bridge (return /
  // direct-click / redirect-to-Tavian all start no session), so his bubble IS
  // the standing greeting and a prompting question is correct. Aldric, Kira and
  // Grolt each stream a real opener right after (char-create / level-up /
  // merchant), so their bubbles are pure fillers.
  const npcWelcomeMessages: Record<string, string> = {
    tavian: 'Welcome, traveler! How may I assist you today?',
    aldric: 'One moment, friend. Let me light the candles and ready the Rite of Awakening.',
    kira: 'One moment. Let me chalk my hands and ready the training ring.',
    grolt: 'Aye, hold on. Let me haul my wares up and clear the counter.',
  };
  const npcEmojis: Record<string, string> = {
    tavian: '🧙‍♂️',
    aldric: '🌟',
    kira: '⚔️',
    grolt: '⚒️',
  };
  const name = npcNames[npc];
  const heroVideo = NPC_HERO_VIDEOS[npc];
  const welcomeMessage = npcWelcomeMessages[npc];
  const emoji = npcEmojis[npc];
  const avatarUrl = NPC_AVATARS[npc];

  return (state: LobbyState) => {
    const mainRoom = state.chatRooms.find((r) => r.id === 'main');
    const isEmptyChat = !mainRoom?.messages?.length;
    const isNewNpc = mainRoom?.type !== npc;

    // Tavian's lines are the only ones that survive a switch to another NPC.
    // Matches on senderId OR type because his welcome bubble sets both while
    // streamed replies are keyed by senderId.
    const preservedTavianHistory = (mainRoom?.messages ?? []).filter(
      (m) => m.senderId === 'tavian' || m.type === 'tavian'
    );

    // Returning to an NPC already greeted in this thread must not stack a
    // second bridge bubble (with its hero video). Only reachable for Tavian,
    // since his are the only messages that persist across a switch.
    const alreadyWelcomed =
      mainRoom?.messages?.some((m) => m.id.startsWith(`welcome-${npc}-`)) ?? false;

    const welcomeMsg =
      (isEmptyChat || isNewNpc) && !alreadyWelcomed
        ? {
            id: `welcome-${npc}-${Date.now()}`,
            senderId: npc,
            senderName: name,
            content: heroVideo ? `[NPC_HERO:${heroVideo}]\n\n${welcomeMessage}` : welcomeMessage,
            timestamp: new Date(),
            type: npc as 'tavian' | 'aldric' | 'kira' | 'grolt',
            avatar: avatarUrl,
            avatarUrl,
            avatarEmoji: emoji,
          }
        : null;

    persistActiveChatRoom('main');

    return {
      mainChatNpc: npc,
      chatRooms: state.chatRooms.map((r) =>
        r.id === 'main'
          ? {
              ...r,
              name,
              participants: [name],
              type: npc,
              // A new NPC's bridge bubble replaces the thread, EXCEPT for
              // Tavian's lines, which carry forward.
              //
              // Tavian is the lobby's narrator and the first-time player's
              // guide, not a transaction: a new player is auto-redirected
              // Tavian -> Aldric moments after his welcome lands, and a blanket
              // replace wiped that welcome (and its hero video) before it could
              // be read. Aldric/Kira/Grolt threads ARE discrete transactions
              // (a creation, a level-up, a purchase) that the next NPC has no
              // reason to carry forward, so those still get dropped.
              //
              // `session-events.aldric-resume.test.ts` already assumed Tavian's
              // greeting survives alongside an Aldric bridge bubble in this
              // room, so this also realigns the store with the resume logic
              // that reads it.
              messages:
                isNewNpc || isEmptyChat
                  ? [...preservedTavianHistory, ...(welcomeMsg ? [welcomeMsg] : [])]
                  : r.messages,
            }
          : r
      ),
      activeChatRoomId: 'main',
    };
  };
}

export const chatSlice: Pick<
  LobbyState,
  | 'chatRooms'
  | 'activeChatRoomId'
  | 'mainChatNpc'
  | 'completedStreamIds'
  | 'activeStreams'
  | 'pendingPartyMessages'
  | 'addChatRoom'
  | 'removeChatRoom'
  | 'updateChatRoom'
  | 'updateChatRoomParticipants'
  | 'switchChatRoom'
  | 'addMessage'
  | 'appendToMessage'
  | 'markRoomRead'
  | 'clearChatHistory'
  | 'finalizeStreamingMessages'
  | 'switchMainNpc'
  | 'setMainChatNpc'
  | 'startAldricChat'
  | 'startKiraChat'
  | 'startGroltChat'
  | 'returnToTavian'
  | 'clearChatRooms'
  | 'setActiveChatRoomId'
> = {
  chatRooms: [
    {
      id: 'main',
      name: 'Tavian',
      type: 'tavian',
      participants: ['Tavian'],
      messages: [],
      unreadCount: 0,
      isTavianPresent: true,
    },
  ],
  activeChatRoomId: 'main',
  mainChatNpc: 'tavian',
  completedStreamIds: {},
  activeStreams: {},
  pendingPartyMessages: {},

  addChatRoom: (room: ChatRoom) => (state: LobbyState) => {
    const pending = state.pendingPartyMessages[room.id];
    const { [room.id]: _flushed, ...clearedPending } = state.pendingPartyMessages;

    if (state.chatRooms.some((r) => r.id === room.id)) {
      // Room already exists — still flush any buffered messages into it
      if (!pending?.length) return {};
      console.log(
        `[addChatRoom] Duplicate room ${room.id}, flushing ${pending.length} pending messages`
      );
      return {
        chatRooms: state.chatRooms.map((r) =>
          r.id === room.id
            ? {
                ...r,
                // Cap on flush: no reveal is in flight during room creation, so
                // no pin to preserve. Keeps every transcript append bounded.
                messages: pruneMessages(
                  [...r.messages, ...pending].sort(
                    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                  ),
                  MAX_MESSAGES_PER_ROOM,
                  null
                ),
              }
            : r
        ),
        pendingPartyMessages: clearedPending,
      };
    }

    // New room — flush any pending messages into it
    let newRoom = room;
    if (pending?.length) {
      console.log(`[addChatRoom] Flushing ${pending.length} pending messages into room ${room.id}`);
      const merged = pruneMessages(
        [...room.messages, ...pending].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        ),
        MAX_MESSAGES_PER_ROOM,
        null
      );
      newRoom = { ...room, messages: merged };
    }

    return {
      chatRooms: [...state.chatRooms, newRoom],
      pendingPartyMessages: pending?.length ? clearedPending : state.pendingPartyMessages,
    };
  },

  removeChatRoom: (roomId: string) => (state: LobbyState) => {
    if (roomId === 'main') return {};
    // Clean up orphaned reveal queue entries for the removed room
    const { [roomId]: _q, ...newRevealQueues } = state.revealQueues;
    const { [roomId]: _r, ...newRevealingIds } = state.revealingMessageIds;
    // Clean up any pending messages for this room
    const { [roomId]: _p, ...newPendingPartyMessages } = state.pendingPartyMessages;
    return {
      chatRooms: state.chatRooms.filter((r) => r.id !== roomId),
      activeChatRoomId: state.activeChatRoomId === roomId ? 'main' : state.activeChatRoomId,
      revealQueues: newRevealQueues,
      revealingMessageIds: newRevealingIds,
      pendingPartyMessages: newPendingPartyMessages,
    };
  },

  updateChatRoom:
    (roomId: string, updates: Partial<Omit<ChatRoom, 'id' | 'messages'>>) =>
    (state: LobbyState) => ({
      chatRooms: state.chatRooms.map((r: ChatRoom) => (r.id === roomId ? { ...r, ...updates } : r)),
    }),

  updateChatRoomParticipants: (roomId: string, participants: string[]) => (state: LobbyState) => ({
    chatRooms: state.chatRooms.map((r: ChatRoom) => (r.id === roomId ? { ...r, participants } : r)),
  }),

  switchChatRoom: (roomId: string) => (state: LobbyState) => {
    const room = state.chatRooms.find((r) => r.id === roomId);
    if (!room) return {};

    // Dump the old room's reveal queue instantly before switching — but
    // PRESERVE a live WS stream if one is in flight there. Truncating a
    // live stream mid-flight would flip isStreaming=false on the bubble;
    // subsequent NpcStreamChunk events would then fail the streaming-bubble
    // lookup and split into a second bubble.
    const oldRoomId = state.activeChatRoomId;
    const oldQueue = state.revealQueues[oldRoomId] ?? [];
    const oldRevealId = state.revealingMessageIds[oldRoomId];
    const oldRoom = state.chatRooms.find((r) => r.id === oldRoomId);
    const oldRevealMsg = oldRevealId
      ? oldRoom?.messages.find((m) => m.id === oldRevealId)
      : undefined;
    const oldRevealIsLive = oldRevealMsg?.isLiveStream === true;
    const hasQueuedWork = oldQueue.length > 0 || (oldRevealId && !oldRevealIsLive);

    let chatRooms = state.chatRooms;
    let revealQueues = state.revealQueues;
    let revealingMessageIds = state.revealingMessageIds;

    if (hasQueuedWork) {
      chatRooms = chatRooms.map((r) => {
        if (r.id !== oldRoomId) return r;
        const finishedMessages = r.messages.map((m) =>
          // Only finish the in-flight reveal if it's NOT a live stream —
          // live streams stay marked so chunks keep landing in the same bubble.
          m.id === oldRevealId && !oldRevealIsLive
            ? { ...m, isStreaming: false, isQueuedReveal: false }
            : m
        );
        const dumped = oldQueue.map((m) => ({ ...m, isStreaming: false, isQueuedReveal: false }));
        // Cap the dumped transcript like every other append site (skipRevealQueue,
        // addMessage, reveal-queue enqueue/advance). A raw concat here let repeated
        // room switches with a wedged/pinned live reveal grow the transcript past
        // MAX_MESSAGES_PER_ROOM unbounded → freeze/OOM on room reopen. Preserve the
        // pinned live-stream bubble when one is in flight.
        return {
          ...r,
          messages: pruneMessages(
            [...finishedMessages, ...dumped],
            MAX_MESSAGES_PER_ROOM,
            oldRevealIsLive ? oldRevealId : null
          ),
        };
      });
      revealQueues = { ...revealQueues, [oldRoomId]: [] };
      // Clear revealingMessageIds only for the canned-reveal case; if the
      // current reveal is a live stream, leave it registered so the bubble
      // keeps its queue identity for ProgressiveReveal's onComplete handler.
      if (!oldRevealIsLive) {
        revealingMessageIds = { ...revealingMessageIds, [oldRoomId]: null };
      }
    }

    persistActiveChatRoom(roomId);

    return {
      activeChatRoomId: roomId,
      chatRooms: chatRooms.map((r) => (r.id === roomId ? { ...r, unreadCount: 0 } : r)),
      revealQueues,
      revealingMessageIds,
    };
  },

  switchMainNpc: (npc: 'tavian' | 'aldric' | 'kira' | 'grolt') => switchMainNpcImpl(npc),

  // These call switchMainNpc's implementation directly (not through the store).
  // The slice typing sees () => void, but the implementation returns a state
  // transformer. Helper avoids verbose casts at each call site.
  startAldricChat: () => (state: LobbyState) => switchMainNpcImpl('aldric')(state),

  startKiraChat: () => (state: LobbyState) => switchMainNpcImpl('kira')(state),

  startGroltChat: () => (state: LobbyState) => switchMainNpcImpl('grolt')(state),

  returnToTavian: () => (state: LobbyState) => switchMainNpcImpl('tavian')(state),

  setMainChatNpc: (npc) => ({ mainChatNpc: npc }),

  clearChatRooms: () => {
    persistActiveChatRoom('main');
    return {
      chatRooms: [
        {
          id: 'main',
          name: 'Tavian',
          type: 'tavian' as const,
          participants: ['Tavian'],
          messages: [],
          unreadCount: 0,
          isTavianPresent: true,
        },
      ],
      activeChatRoomId: 'main',
      mainChatNpc: 'tavian' as const,
      revealQueues: {},
      revealingMessageIds: {},
      pendingPartyMessages: {},
    };
  },

  setActiveChatRoomId: (roomId) => {
    const resolved = roomId ?? 'main';
    persistActiveChatRoom(resolved);
    return { activeChatRoomId: resolved };
  },

  addMessage: (roomId, message) => (state: LobbyState) => {
    const roomExists = state.chatRooms.some((r) => r.id === roomId);

    // Buffer messages for party rooms that don't exist yet (race: messages arrive before room creation)
    if (!roomExists && roomId.startsWith('party-')) {
      console.warn(`[addMessage] Buffered message for pending room: ${roomId}`);
      const existing = state.pendingPartyMessages[roomId] ?? [];
      if (existing.length >= MAX_PENDING_PER_ROOM) {
        console.warn(`[addMessage] Pending buffer full for ${roomId}, dropping oldest`);
        return {
          pendingPartyMessages: {
            ...state.pendingPartyMessages,
            [roomId]: [...existing.slice(1), message],
          },
        };
      }
      return {
        pendingPartyMessages: {
          ...state.pendingPartyMessages,
          [roomId]: [...existing, message],
        },
      };
    }

    const isActive = state.activeChatRoomId === roomId;
    // Pinned-aware pruning everywhere (see prune-messages.ts): a plain
    // slice here evicted the live-stream bubble the reveal-queue flush had
    // just rescued to index 0, killing the rest of the stream.
    const pinnedId = state.revealingMessageIds[roomId];
    return {
      chatRooms: state.chatRooms.map((r: ChatRoom) => {
        if (r.id !== roomId) return r;

        // Prune first to avoid searching through messages that will be discarded
        const messages = pruneMessages(r.messages, MAX_MESSAGES_PER_ROOM, pinnedId);

        // Insert message in timestamp order (optimized for near-sorted data)
        const newTimestamp = new Date(message.timestamp).getTime();
        let newMessages: typeof messages;

        // Fast path: if message is newer than last, just append
        if (
          messages.length === 0 ||
          newTimestamp >= new Date(messages[messages.length - 1].timestamp).getTime()
        ) {
          newMessages = [...messages, message];
        } else {
          // Slow path: binary search for insert position
          let low = 0;
          let high = messages.length;
          while (low < high) {
            const mid = (low + high) >>> 1;
            if (new Date(messages[mid].timestamp).getTime() < newTimestamp) {
              low = mid + 1;
            } else {
              high = mid;
            }
          }
          newMessages = [...messages.slice(0, low), message, ...messages.slice(low)];
        }

        return {
          ...r,
          // Final prune after insertion (in case we added one more)
          messages: pruneMessages(newMessages, MAX_MESSAGES_PER_ROOM, pinnedId),
          unreadCount: isActive ? 0 : r.unreadCount + 1,
        };
      }),
    };
  },

  appendToMessage: (roomId, messageId, content) => (state: LobbyState) => ({
    chatRooms: state.chatRooms.map((r: ChatRoom) =>
      r.id === roomId
        ? {
            ...r,
            messages: r.messages.map((m: ChatMessage) => {
              if (m.id !== messageId) return m;
              const rawContent = (m.rawContent || m.content) + content;
              return {
                ...m,
                rawContent,
                content: cleanNpcResponse(rawContent),
              };
            }),
          }
        : r
    ),
  }),

  markRoomRead: (roomId) => (state: LobbyState) => ({
    chatRooms: state.chatRooms.map((r: ChatRoom) =>
      r.id === roomId ? { ...r, unreadCount: 0 } : r
    ),
  }),

  clearChatHistory: (roomId) => (state: LobbyState) => ({
    chatRooms: state.chatRooms.map((r: ChatRoom) =>
      !roomId || r.id === roomId ? { ...r, messages: [], unreadCount: 0 } : r
    ),
  }),

  finalizeStreamingMessages: () => (state: LobbyState) => {
    // Reconnect drops `activeStreams`, so any bubble still flagged live is an
    // orphan whose NpcStreamEnd can never arrive. Settle it (drop the live
    // flags, keep the text) so `StreamingMarkdown`/@llm-ui isn't asked to keep
    // revealing a dead stream forever, and clear the now-dangling reveal
    // pointers. The `hasLive` guard keeps clean rooms referentially stable.
    //
    // ALSO sweep `pendingPartyMessages`: `party-stream-start` buffers an
    // `isStreaming:true` placeholder there when the `party-*` room doesn't exist
    // yet. This finalizer runs at connect-start AFTER `activeStreams` is cleared,
    // so a buffered placeholder is just as orphaned as a mounted one: its
    // `party-stream-end` can no longer settle it, and `addChatRoom` would later
    // flush it into the room verbatim, mounting a permanent typing bubble. Settle
    // the buffer too. (Scoped to the finalizer, not `addChatRoom`, so a normal
    // first-connect stream that legitimately continues into a new room is
    // untouched.)
    const roomsHaveLive = state.chatRooms.some((r) =>
      r.messages.some((m) => m.isStreaming || m.isLiveStream || m.isQueuedReveal)
    );
    const pendingHaveLive = Object.values(state.pendingPartyMessages).some((msgs) =>
      msgs.some((m) => m.isStreaming || m.isLiveStream || m.isQueuedReveal)
    );
    if (!roomsHaveLive && !pendingHaveLive) return {};

    const settle = (m: ChatMessage): ChatMessage =>
      m.isStreaming || m.isLiveStream || m.isQueuedReveal
        ? { ...m, isStreaming: false, isLiveStream: false, isQueuedReveal: false }
        : m;

    const result: Partial<LobbyState> = { revealingMessageIds: {} };
    if (roomsHaveLive) {
      result.chatRooms = state.chatRooms.map((r) => {
        const hasLive = r.messages.some((m) => m.isStreaming || m.isLiveStream || m.isQueuedReveal);
        return hasLive ? { ...r, messages: r.messages.map(settle) } : r;
      });
    }
    if (pendingHaveLive) {
      result.pendingPartyMessages = Object.fromEntries(
        Object.entries(state.pendingPartyMessages).map(([roomId, msgs]) => [
          roomId,
          msgs.map(settle),
        ])
      );
    }
    return result;
  },
};
